import { Badge } from '@/components/ui/badge';

import { INVOICE_INFO_GROUPS, infoFieldLabel } from '../lib/invoice-detail-fields';
import { invoiceStatusLabel } from '../lib/invoice-label';
import { PricingStateBadge } from './pricing-state-badge';

import type { InvoiceRow } from '../types';

// The INVOICE INFORMATION block of the detail drawer: the status chip (with
// the purchase-side Raised verb), the fixation-driven Provisional / Final
// chip, and the grouped field grid.

export function InvoiceInformationSection({ invoice }: Readonly<{ invoice: InvoiceRow }>) {
  const lastGroup = INVOICE_INFO_GROUPS.length - 1;
  return (
    <section aria-labelledby="invoice-summary-heading" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 id="invoice-summary-heading" className="text-sm font-semibold">
          INVOICE INFORMATION
        </h3>
        <div className="flex items-center gap-2">
          {invoice.invoice_direction !== null ? (
            <PricingStateBadge
              blOrderId={invoice.bl_order_id}
              direction={invoice.invoice_direction}
            />
          ) : null}
          {invoice.status !== null ? (
            <Badge variant="outline">{invoiceStatusLabel(invoice)}</Badge>
          ) : null}
        </div>
      </div>
      {INVOICE_INFO_GROUPS.map((group, index) => (
        <dl
          key={group.key}
          className={`grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2 ${index > 0 ? 'border-border border-t pt-3' : ''}`}
        >
          {group.fields.map((field) => (
            // Launchpad keeps every INVOICE INFORMATION field visible, rendering
            // an em-dash for empties rather than dropping the row.
            <DescriptionRow
              key={infoFieldLabel(field, invoice)}
              label={infoFieldLabel(field, invoice)}
              value={field.value(invoice) ?? '—'}
            />
          ))}
          {index === lastGroup && invoice.is_placeholder ? (
            <DescriptionRow label="Placeholder" value="Yes — awaiting invoice details" />
          ) : null}
        </dl>
      ))}
    </section>
  );
}

export function DescriptionRow({
  label,
  value,
  chip = false,
}: Readonly<{
  label: string;
  value: string | null;
  chip?: boolean;
}>) {
  if (value === null) return null;
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{chip ? <Badge variant="outline">{value}</Badge> : value}</dd>
    </>
  );
}
